import { QuantityTier } from './types'

function formatTierLabel(tier: QuantityTier): string {
  return `${tier.min_quantity} Pack${tier.min_quantity > 1 ? 's' : ''}`
}

export const buyMoreEmptyLabel = 'Buy more to unlock savings'
export const buyMoreSuccessLabel = 'Savings unlocked!'
export const buyMoreGiftsSuccessLabel = 'Savings & FREE Gifts unlocked!'

export default class TierProgressIndicator extends HTMLElement {
  private _totalQuantity = 0
  private _showDiscounts = true
  public basePrice = 0
  public tiers: QuantityTier[] = []

  public get showDiscounts() {
    return this._showDiscounts
  }

  public set showDiscounts(value: boolean) {
    this._showDiscounts = value
    this.render()
  }

  public get tier(): QuantityTier | undefined {
    let maxTier: QuantityTier | undefined

    for (const tier of this.tiers) {
      if (!maxTier || tier.min_quantity > maxTier.min_quantity) {
        maxTier = tier
      }
    }

    return maxTier
  }

  public get totalQuantity(): number {
    return this._totalQuantity
  }

  public set totalQuantity(value: number) {
    this._totalQuantity = value
    this.render()
  }

  public get tierLabel(): string {
    if (this.tier) {
      return formatTierLabel(this.tier)
    }
    return ''
  }

  private get nextTier(): QuantityTier | undefined {
    for (let i = this.tiers.length - 1; i >= 0; i--) {
      const tier = this.tiers[i]
      if (tier.min_quantity > this.totalQuantity) {
        return tier
      }
    }
  }

  private get currentTier(): QuantityTier | undefined {
    return this.tiers.find(tier => tier.min_quantity <= this.totalQuantity)
  }

  public get buyMoreLabel(): string {
    const currentTier = this.currentTier
    const nextTier = this.nextTier ?? currentTier

    if (!nextTier) {
      return buyMoreEmptyLabel
    }

    const nextSavings = Number(nextTier.savings_percentage)
    const currentSavings = Number(currentTier?.savings_percentage ?? '0')

    const savings =
      this.showDiscounts &&
      !isNaN(nextSavings) &&
      !isNaN(currentSavings) &&
      nextSavings > currentSavings &&
      nextSavings - currentSavings

    const packsNeeded = nextTier.min_quantity - this.totalQuantity

    if (packsNeeded <= 0) {
      return nextTier.gift_bundle ? buyMoreGiftsSuccessLabel : buyMoreSuccessLabel
    }

    const hasGifts = !!nextTier.gift_bundle
    const messageParts: string[] = []

    if (savings) {
      messageParts.push(`${savings}% more off`)
    }

    if (hasGifts) {
      messageParts.push('more FREE gifts')
    }

    if (messageParts.length === 0) {
      return `Buy ${packsNeeded} more for more savings`
    }

    const details = ' for ' + messageParts.join(' and ')
    return `Buy ${packsNeeded} more${details}`
  }

  connectedCallback() {
    this.initState()
  }

  private initState() {
    const quantityAttr = this.getAttribute('total-quantity')
    if (quantityAttr) {
      this.totalQuantity = parseInt(quantityAttr, 10)
    }

    const basePriceAttr = this.getAttribute('base-price')
    if (basePriceAttr) {
      this.basePrice = parseFloat(basePriceAttr)
    }

    const tiersAttr = this.getAttribute('tiers')
    if (tiersAttr) {
      this.tiers = JSON.parse(tiersAttr) as QuantityTier[]
    }

    this.render()
  }

  public get progress(): number {
    if (this.totalQuantity === 0) {
      return 0
    }

    const upper = this.tier?.min_quantity ?? this.totalQuantity

    return Math.min(100, (100 * this.totalQuantity) / upper)
  }

  private render() {
    const progressBar = this.querySelector<HTMLDivElement>('.progress-bar')
    if (progressBar) {
      progressBar.style.setProperty('--progress', `${this.progress}%`)
    }

    const nextTierLabel = this.querySelector<HTMLElement>('.next-tier')
    if (nextTierLabel) {
      nextTierLabel.textContent = this.tierLabel
    }

    const buyMoreLabel = this.querySelector<HTMLElement>('.buy-more')
    if (buyMoreLabel) {
      buyMoreLabel.textContent = this.buyMoreLabel
    }
  }
}

if (!customElements.get('tier-progress-indicator')) {
  customElements.define('tier-progress-indicator', TierProgressIndicator)
}
